/**
 * MODULE      : offlineQueue
 * OBJECTIF    : File d'attente hors-ligne persistée dans IndexedDB via
 *               `localforage`. Toute mutation POST/PATCH/DELETE peut être
 *               mise en file quand le réseau est indisponible et rejouée
 *               automatiquement au retour de la connexion.
 * ARCHITECTURE: Pure JS, aucune dépendance UI. Écoute `online`/`offline`
 *               window events. Callbacks pour progression.
 * API         :
 *   enqueue({method, url, data, meta}) -> id
 *   pending() -> Promise<items[]>
 *   flush() -> Promise<{sent, failed}>
 *   subscribe(cb) : cb(state)  // state = {pending, syncing, lastSyncAt, isOnline}
 * RÈGLES MÉTIER :
 *   - Idempotency-Key généré et envoyé pour éviter doublons serveur.
 *   - Chaque item porte un compteur d'échecs (attempts, max 5).
 *   - Aucun conflit : la version la plus récente locale gagne, et si
 *     le serveur retourne 409 on marque l'item comme conflict.
 */
import localforage from "localforage";
import axios from "axios";

const store = localforage.createInstance({
  name: "elia-offline",
  storeName: "queue",
  description: "ELIA offline mutation queue",
});

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const MAX_ATTEMPTS = 5;

const listeners = new Set();
let state = {
  pending: 0,
  syncing: false,
  lastSyncAt: null,
  isOnline: typeof navigator !== "undefined" ? navigator.onLine : true,
};

function notify() {
  listeners.forEach((cb) => {
    try { cb(state); } catch (_) {}
  });
}

async function computePending() {
  const keys = await store.keys();
  state.pending = keys.length;
  notify();
  return keys;
}

function uuid() {
  return crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export async function enqueue({ method = "POST", url, data = null, meta = {} }) {
  const id = uuid();
  const item = {
    id,
    method: method.toUpperCase(),
    url,
    data,
    meta,
    attempts: 0,
    createdAt: new Date().toISOString(),
    conflict: false,
  };
  await store.setItem(id, item);
  await computePending();
  // Try to flush immediately if online
  if (state.isOnline) flush().catch(() => {});
  return id;
}

export async function pending() {
  const items = [];
  await store.iterate((v) => { items.push(v); });
  return items.sort((a, b) => a.createdAt.localeCompare(b.createdAt));
}

async function sendOne(item) {
  const authToken = typeof localStorage !== "undefined"
    ? localStorage.getItem("token") || localStorage.getItem("auth_token")
    : null;
  const headers = {
    "Content-Type": "application/json",
    "Idempotency-Key": item.id,
    ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
  };
  const cfg = {
    method: item.method,
    url: `${BACKEND_URL}${item.url}`,
    headers,
    data: item.data,
    timeout: 20000,
  };
  const r = await axios(cfg);
  return r.status;
}

export async function flush() {
  if (state.syncing) return { sent: 0, failed: 0 };
  const items = await pending();
  if (items.length === 0) {
    state.isOnline = navigator.onLine;
    notify();
    return { sent: 0, failed: 0 };
  }
  state.syncing = true;
  notify();
  let sent = 0, failed = 0;
  for (const item of items) {
    try {
      const status = await sendOne(item);
      if (status >= 200 && status < 300) {
        await store.removeItem(item.id);
        sent++;
      } else if (status === 409) {
        // Conflict : marquer et ne pas retenter
        item.conflict = true;
        item.attempts = MAX_ATTEMPTS;
        await store.setItem(item.id, item);
        failed++;
      } else {
        item.attempts += 1;
        if (item.attempts >= MAX_ATTEMPTS) {
          await store.removeItem(item.id);
        } else {
          await store.setItem(item.id, item);
        }
        failed++;
      }
    } catch (e) {
      // Network error → keep in queue
      item.attempts += 1;
      if (item.attempts >= MAX_ATTEMPTS) {
        await store.removeItem(item.id);
      } else {
        await store.setItem(item.id, item);
      }
      failed++;
      // If we've lost network, stop trying
      if (!navigator.onLine) break;
    }
  }
  state.syncing = false;
  state.lastSyncAt = new Date().toISOString();
  await computePending();
  return { sent, failed };
}

export function subscribe(cb) {
  listeners.add(cb);
  cb(state);
  return () => listeners.delete(cb);
}

export function getState() {
  return { ...state };
}

// Auto-flush when connectivity returns
if (typeof window !== "undefined") {
  window.addEventListener("online", () => {
    state.isOnline = true;
    notify();
    flush().catch(() => {});
  });
  window.addEventListener("offline", () => {
    state.isOnline = false;
    notify();
  });
  // Initial compute
  computePending().catch(() => {});
  // Periodic retry every 60 s
  setInterval(() => {
    if (state.isOnline && !state.syncing) flush().catch(() => {});
  }, 60000);
}

export default { enqueue, pending, flush, subscribe, getState };
